import React, { createContext, ReactNode, useContext, useReducer } from 'react';

import { TravelEligibility } from 'leash/types';
import { TravelPlan, TravelProposal, TravelSequence } from 'types';

// ********************************************************************************
// Reference documentation:
// https://kentcdodds.com/blog/how-to-use-react-context-effectively
// ********************************************************************************

interface UseDetailsState {
  travelPlan: TravelPlan | undefined;
  sequences?: TravelSequence[];
  travelEligibility: TravelEligibility[];
  travelEligibilitySelected: TravelEligibility | undefined;
}

type Dispatch = (action: DetailsActions) => void;

export type DetailsActions =
  | { type: 'SET_TRAVEL_PLAN'; travelPlan: TravelPlan }
  | { type: 'SET_TRAVEL_PROPOSAL'; travelProposal: TravelProposal }
  | { type: 'SET_CONFIRMED' }
  | { type: 'SET_REJECTED_BY'; rejectedBy: string }
  | { type: 'SET_WORK_FLOW_STATUS'; workflowStatus: string }
  | { type: 'SET_TRAVEL_ELIGIBILITY'; travelEligibility: TravelEligibility[] }
  | {
      type: 'SET_TRAVEL_ELIGIBILITY_SELECTED';
      travelEligibilitySelected: TravelEligibility | undefined;
    }
  | { type: 'SET_SEQUENCES'; sequences: TravelSequence[] };

export const detailsReducer = (
  state: UseDetailsState,
  action: DetailsActions,
): UseDetailsState => {
  switch (action.type) {
    case 'SET_TRAVEL_PLAN': {
      const { travelPlan } = action;
      return { ...state, travelPlan, sequences: travelPlan.travelSequences };
    }
    case 'SET_TRAVEL_PROPOSAL': {
      const { travelProposal } = action;
      if (state.travelPlan) {
        state.travelPlan.travelPackage.travelProposal = travelProposal;
      }
      return { ...state };
    }
    case 'SET_WORK_FLOW_STATUS': {
      const { workflowStatus } = action;
      if (state.travelPlan) {
        state.travelPlan.travelPackage.workflowStatus = workflowStatus;
      }
      return { ...state };
    }
    case 'SET_CONFIRMED': {
      if (state.travelPlan) {
        state.travelPlan.travelPackage.workflowStatus = 'Confirmed';
      }
      return { ...state };
    }
    case 'SET_REJECTED_BY': {
      const { rejectedBy } = action;
      if (state.travelPlan?.travelPackage.travelProposal) {
        state.travelPlan.travelPackage.travelProposal.departureTime = null;
      }
      if (state.travelPlan) {
        state.travelPlan.travelPackage.workflowStatus = `rejected by ${rejectedBy}`;
      }
      return { ...state };
    }
    case 'SET_TRAVEL_ELIGIBILITY': {
      const { travelEligibility } = action;
      return { ...state, travelEligibility };
    }
    case 'SET_TRAVEL_ELIGIBILITY_SELECTED': {
      const { travelEligibilitySelected } = action;
      return { ...state, travelEligibilitySelected };
    }
    case 'SET_SEQUENCES': {
      const { sequences } = action;
      return { ...state, sequences };
    }
    default: {
      return state;
    }
  }
};

const DetailsStateContext = createContext<
  { state: UseDetailsState; dispatch: Dispatch } | undefined
>(undefined);

interface DetailsProviderProps {
  children: ReactNode;
  reducerInitialState?: UseDetailsState;
}

export function DetailsProvider(props: DetailsProviderProps) {
  const { children, reducerInitialState } = props;
  const initialState = reducerInitialState ?? {
    travelPlan: undefined,
    sequences: [],
    travelEligibility: [],
    travelEligibilitySelected: undefined,
  };
  const [state, dispatch] = useReducer(detailsReducer, initialState);

  const value = { state, dispatch };

  return (
    <DetailsStateContext.Provider value={value}>{children}</DetailsStateContext.Provider>
  );
}

export function useDetailsState() {
  const context = useContext(DetailsStateContext);
  if (!context) {
    throw new Error('useDetailsState must be used within a DetailsProvider');
  }

  return context;
}
